import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCategoryAndTitle } from 'utils';
import routeCodes from 'common/constants/route-codes';
import { useDishes } from 'redux/dishes/use-dishes';
import { useCompany } from 'redux/company/use-company';
import {
  useCurrentRestaurant,
  useRestaurant,
} from 'redux/restaurant/use-restaurant';
import AuthService from 'services/Auth';

import {
  setModalData,
  setEditableItem,
  getCategories,
  setCurrentRestaurant,
  getStripeLink,
  resetState as resetStateAction,
} from './actions';
import {
  modalDataSelector,
  editableDataSelector,
  categoriesList,
  categoriesLoadingSelector,
  currentRestaurantSelector,
} from './selectors';
import UsersApi from 'libs/api/users';
import { useSpecialMenu } from 'redux/special_menu/use-special-menu';
import { useDishOptions } from 'redux/dishes_options/use-dish-options';
import { useDishCategory } from '../dishes_categories/use-dish-category';
const userApi = new UsersApi();

export const useCommon = () => {
  const modalData = useSelector(modalDataSelector);
  const editableItem = useSelector(editableDataSelector);
  const currentRestaurant = useSelector(currentRestaurantSelector);
  const { handleDeleteRestaurant } = useRestaurant();
  const { handleCreateOrUpdate } = useCurrentRestaurant();
  const { handleDeleteCompany } = useCompany();
  const { handleDeleteDish } = useDishes();
  const { handleDeleteSpecialMenu } = useSpecialMenu();
  const { handleDelete: handleDeleteDishOption } = useDishOptions();
  const { handleDeleteDishCategory } = useDishCategory();
  const dispatch = useDispatch();

  const handleSetModalData = (payload) => {
    dispatch(setModalData(payload));
  };

  const handleResetModalData = () => {
    dispatch(
      setModalData({
        open: false,
        variant: 'primary',
        title: '',
        body: '',
        itemData: {},
      }),
    );
  };

  const handleGetStripeLink = () => {
    dispatch(getStripeLink());
  };

  const handleSubmitModalAction = () => {
    if (modalData.variant === 'delete') {
      const [category, id] = getCategoryAndTitle(modalData.itemData);
      if (category.includes('restaurants')) {
        handleDeleteRestaurant(id);
      }
      if (category.includes('firms')) {
        handleDeleteCompany(id);
      }
      if (category.includes('dishes')) {
        handleDeleteDish(id);
      }
      if (category.includes('special-menu')) {
        handleDeleteSpecialMenu(id);
      }
      if (category.includes('options')) {
        handleDeleteDishOption(id);
      }
      if (category.includes('team')) {
        // TODO team delete callback
        console.log('delete', category, id);
        (async () => {
          try {
            await userApi.destroy(id);
            handleResetModalData();
          } catch (e) {

          }
        })();
      }
      if (category.includes('dish-categories')) {
        handleDeleteDishCategory(id);
      }
    }

    if (modalData.variant === 'update') {
      const { id, data } = modalData.itemData;
      handleCreateOrUpdate(data, id, routeCodes.home, true, { restaurantDisabledAction: true });
    }
  };

  const handleSetCurrentRestaurant = payload => {
    AuthService.setCurrentRestaurant(payload?.id);
    dispatch(setCurrentRestaurant(payload));
  };

  const handleSetEditableItem = useCallback((payload) => {
    dispatch(setEditableItem(payload));
  }, [dispatch]);

  const resetState = useCallback(() => {
    dispatch(resetStateAction());
  }, [dispatch]);

  return {
    modalData,
    editableItem,
    handleSetEditableItem,
    handleResetModalData,
    handleSetModalData,
    handleSubmitModalAction,
    handleSetCurrentRestaurant,
    currentRestaurant,
    handleGetStripeLink,
    resetState,
  };
};

export const useCategories = () => {
  const dispatch = useDispatch();
  const list = useSelector(categoriesList);
  const categoriesLoading = useSelector(categoriesLoadingSelector);

  const handleGetList = (payload = {
    params: {
      per: 10000,
    }
  }) => {
    dispatch(getCategories(payload));
  };

  return { list, handleGetList, categoriesLoading };
};
